// CreateInspection — "New inspection" side panel per the design. Fields live in
// the shared InspectionFormFields (design order); this drawer owns the draft,
// validation and the two submit paths: "Create inspection" → POST
// /inspections/create (name/form/project/level required), "Save as draft" →
// POST /inspections/save-draft (only the name is required).
//
// The project defaults to the company's public project (projectType 'public'
// from /projects/selection) once the options arrive, unless the user already
// picked one — or the caller passed `defaultProjectId`, which simply seeds that
// project instead. The select stays fully editable either way (preselect only,
// by decision — nothing here locks it). Editing an existing inspection is
// EditInspection's job (it saves through PATCH /inspections/update/:id and does
// not reuse this field bag).

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button, Drawer } from '@/components/common';
import {
  useCreateInspectionMutation,
  useSaveDraftInspectionMutation,
} from '@/api/endpoints/inspectionApi';
import InspectionFormFields, {
  emptyInspectionDraft,
  isWindowInvalid,
  useInspectionFormData,
  type InspectionDraft,
} from './InspectionFormFields';

interface CreateInspectionProps {
  open: boolean;
  onClose: () => void;
  /** Project id to PRESELECT, overriding the public-project default — set by the
   *  project detail page's Inspections tab, which hands it over through the
   *  router state. A starting value, not a constraint: the select stays
   *  editable. The id is expected to be in /projects/selection (it is the same
   *  company project list the detail page was reached through), so no option is
   *  injected for it — per the repo's no-defensive-fallback rule. */
  defaultProjectId?: string;
}

/** This drawer's create/save-draft field bag — empty optionals are stripped. */
const buildInspectionFields = (draft: InspectionDraft) => ({
  title: draft.title.trim(),
  form: draft.form || undefined,
  project: draft.project || undefined,
  description: draft.description.trim() || undefined,
  priority: draft.priority,
  tags: draft.tagIds.length ? draft.tagIds : undefined,
  startDate: draft.startDate || undefined,
  endDate: draft.endDate || undefined,
  companyLevel: draft.companyLevel || undefined,
  companyNode: draft.companyNode ? [draft.companyNode] : undefined,
  location: draft.location.trim() || undefined,
  coordinates: draft.coordinates.trim() || undefined,
  // Auto-fill only applies (and is only shown) with a level + node picked —
  // a stale toggle from a since-cleared node is never sent.
  autoFill: draft.companyLevel && draft.companyNode ? draft.autoFill : undefined,
});

const CreateInspection = ({ open, onClose, defaultProjectId }: CreateInspectionProps) => {
  const { t } = useTranslation();

  const [draft, setDraft] = useState(emptyInspectionDraft);
  // Which button was pressed — drives whose validation errors are shown.
  const [attempt, setAttempt] = useState<'create' | 'draft' | null>(null);

  const data = useInspectionFormData(open, draft.companyLevel);
  const [createInspection, { isLoading: creating }] = useCreateInspectionMutation();
  const [saveDraftInspection, { isLoading: savingDraft }] = useSaveDraftInspectionMutation();

  const patch = (changes: Partial<InspectionDraft>) => setDraft((d) => ({ ...d, ...changes }));

  // Re-seed each time the drawer opens; discard leftovers on close. A caller's
  // preselected project is seeded HERE rather than in its own effect — this one
  // wipes the draft on every open, so a preselection applied anywhere else is
  // clobbered.
  useEffect(() => {
    if (open) {
      setDraft({ ...emptyInspectionDraft, project: defaultProjectId ?? null });
      setAttempt(null);
    }
  }, [open, defaultProjectId]);

  // Default the project to the company's public one once the options arrive —
  // but never clobber a pick the user already made.
  useEffect(() => {
    if (!open || defaultProjectId) return;
    setDraft((d) => {
      if (d.project) return d;
      const publicProject = data.projects.find((p) => p.projectType === 'public');
      return publicProject ? { ...d, project: publicProject.id } : d;
    });
  }, [open, data.projects, defaultProjectId]);

  // Create requires name/form/project/level (and a sane window when set);
  // Save-as-draft only ever requires the name.
  const titleMissing = !draft.title.trim();
  const createInvalid =
    titleMissing || !draft.form || !draft.project || !draft.companyLevel || isWindowInvalid(draft);

  const create = async () => {
    setAttempt('create');
    if (createInvalid) return;
    try {
      await createInspection(buildInspectionFields(draft)).unwrap();
      toast.success(t('inspection_created', { name: draft.title.trim() }));
      onClose();
    } catch {
      toast.error(t('error_generic'));
    }
  };

  const saveDraft = async () => {
    setAttempt('draft');
    if (titleMissing) return;
    try {
      await saveDraftInspection(buildInspectionFields(draft)).unwrap();
      toast.success(t('inspection_draft_saved', { name: draft.title.trim() }));
      onClose();
    } catch {
      toast.error(t('error_generic'));
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      icon='clipboard-plus'
      title={t('inspection_create_title')}
      subtitle={t('inspection_create_subtitle')}
      footer={
        <>
          <Button variant='secondary' onClick={onClose} disabled={creating || savingDraft}>
            {t('button_cancel')}
          </Button>
          <Button
            variant='secondary'
            iconStart='file-pen'
            onClick={saveDraft}
            loading={savingDraft}
            disabled={creating}
          >
            {t('inspection_save_draft')}
          </Button>
          <Button iconStart='plus' onClick={create} loading={creating} disabled={savingDraft}>
            {t('inspection_create')}
          </Button>
        </>
      }
    >
      <InspectionFormFields
        draft={draft}
        patch={patch}
        data={data}
        showRequiredErrors={attempt === 'create'}
        showTitleError={attempt !== null && titleMissing}
      />
    </Drawer>
  );
};

export default CreateInspection;
